import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { markAudioUnlocked, resetNotificationSound } from '@/lib/notificationSound';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('shine_user');
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('shine_token', res.data.token);
    localStorage.setItem('shine_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    // The submit click that triggered this call IS a user gesture, but it
    // bubbled through the document before AppLayout mounted. Mark audio
    // unlocked explicitly so the first incoming notification plays.
    try { markAudioUnlocked(); } catch (_) { /* silent */ }
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('shine_token');
    localStorage.removeItem('shine_user');
    try { resetNotificationSound(); } catch (_) { /* silent */ }
    setUser(null);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('shine_user', JSON.stringify(res.data));
    } catch (_) { /* ignore */ }
  }, []);

  // After login, also hydrate effective_access from /auth/me so the first
  // render has the full permission matrix (login returns the compact user).
  useEffect(() => {
    if (localStorage.getItem('shine_token') && user && !user.effective_access) {
      refreshMe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localStorage.getItem('shine_token') && !user) {
      refreshMe();
    }
  }, [refreshMe, user]);

  const isSuperAdmin = user?.role === 'Owner';
  // `isOwner` keeps its existing semantic (top-level admin powers) — now includes the new Admin role.
  const isOwner = user?.role === 'Owner' || user?.role === 'Admin';
  const isAdmin = user?.role === 'Admin';
  const isClient = user?.role === 'Client';
  const isSales = user?.role === 'Sales';
  const isInternal = user && user.role !== 'Client';
  const isManager = user && (user.role === 'Owner' || user.role === 'Admin' || user.role === 'Operations');
  const permissions = user?.permissions || {};
  // Canonical access check: `effective_access` is the whole permission matrix
  // resolved server-side (defaults + overrides, Owner/Admin bypass). Prefer
  // it; fall back to legacy perms + Owner/Admin bypass for safety during
  // rollout. Pages should ALWAYS use has() — never role === '…' checks.
  const effective = user?.effective_access || {};
  const has = (key) => {
    if (isOwner) return true;
    if (key in effective) return !!effective[key];
    return !!permissions[key];
  };
  const hasPerm = has;  // backward-compat alias

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, isOwner, isSuperAdmin, isAdmin, isClient, isSales, isInternal, isManager, permissions, effective, has, hasPerm, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
